import Image from 'next/image';
import { Box, Heading, Icon, Modal, Paragraph, Text, TextButton, Upsell } from '@zillow/constellation-10';
import {
  IconDollarSignCircleFilled,
  IconHouseUserFilled,
  IconProfileFilled,
  IconSignatureFilled,
  IconYardSignFilled,
} from '@zillow/constellation-icons';

import { LoadingButton, LoadingParagraph, LoadingText, shimmerStyles } from '@zg-rentals/loading-components';
import { stringHelper } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerAccountViewZrmPropertiesWebApiHandler_added,
  useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import propertyUtils from '../../../../utils/propertyUtils';

import { RENTAL_HERO_ILLUSTRATION_URL } from '../../../../constants/DigitalLibraryConstants';
import PropertyConstants from '../../../../constants/PropertyConstants';

import {
  emitAddLeaseEvent,
  emitCreateListingActionEvent,
  emitInviteToApplyEvent,
  emitSetUpPaymentsEvent,
} from '../../../../analytics/events/selectAvailableActions';
import { useAuth } from '../../../AuthProvider';

import type { AddPropertyDetailsState } from './AddPropertyDetailsModal';

const UpsellSkeleton = () => (
  <Box
    css={{
      display: 'flex',
      gap: '300',
      padding: '400',
      border: '1px solid token(colors.border.muted)',
      borderRadius: '300',
    }}
  >
    <LoadingText isLoading={true} textStyle="heading-lg" loadingWidth="24px">
      &nbsp;
    </LoadingText>
    <Box css={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '200' }}>
      <LoadingText isLoading={true} textStyle="heading-xs" loadingWidth="55%">
        &nbsp;
      </LoadingText>
      <LoadingParagraph isLoading={true} textStyle="body-sm" loadingWidth="90%">
        &nbsp;
      </LoadingParagraph>
      <LoadingButton isLoading={true} loadingWidth="80px">
        &nbsp;
      </LoadingButton>
    </Box>
  </Box>
);

type Props = AddPropertyDetailsState & {
  isOpen: boolean;
  onClose: () => void;
  multiplexBuildingSessionId?: string;
  firstUnitListingAlias?: string;
  onAddTenantClick: (listingAlias: string) => void;
};

const PropertyAddedSuccessStep = ({
  isOpen,
  listingAlias,
  multiplexBuildingSessionId,
  firstUnitListingAlias,
  onClose,
  onAddTenantClick,
}: Props): JSX.Element => {
  const { user } = useAuth?.() ?? {};

  const effectiveListingAlias = firstUnitListingAlias || listingAlias;

  const { data: webAnalyticsData } = useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
    { listingAlias: effectiveListingAlias!, includeLeases: false },
    { enabled: Boolean(effectiveListingAlias) },
  );

  const { data: addedData } = useRentalManagerAccountViewZrmPropertiesWebApiHandler_added(
    { listingAlias: effectiveListingAlias!, multiplexBuildingSessionId },
    {
      enabled: Boolean(effectiveListingAlias),
      meta: {
        tags: ['property-added'],
        context: { api: 'property-added', details: 'account view property added success actions' },
      },
    },
  );

  const { actions, address, propertyType, numUnits } = addedData ?? {};

  const { formattedStreet, formattedCityStateZip } = address
    ? propertyUtils.formatAddress(address)
    : { formattedStreet: undefined, formattedCityStateZip: undefined };
  const formattedAddress = [formattedStreet, formattedCityStateZip].filter(Boolean).join(', ');

  const displayPropertyType = propertyType
    ? (PropertyConstants.PROPERTY_TYPES_BY_ENUM[propertyType] ?? propertyType)
    : '';
  const propertyTypeLine =
    numUnits && numUnits > 1
      ? `${displayPropertyType} • ${numUnits} ${stringHelper.pluralizeWord(numUnits, 'unit', 'units')}`
      : displayPropertyType;

  const makeActionHandler =
    (
      emitFn: (args: { user: NonNullable<typeof user>; currentProperty: NonNullable<typeof webAnalyticsData> }) => void,
      url: string | undefined,
    ) =>
    () => {
      if (user && webAnalyticsData) {
        emitFn({ user, currentProperty: webAnalyticsData });
      }
      if (url) {
        window.location.href = url;
      }
    };

  const handleCreateListingClick = makeActionHandler(emitCreateListingActionEvent, actions?.listing.url);
  const handleInviteToApplyClick = makeActionHandler(emitInviteToApplyEvent, actions?.applications.url);
  const handleAddLeaseClick = makeActionHandler(emitAddLeaseEvent, actions?.leases.url);
  const handleSetUpPaymentsClick = makeActionHandler(emitSetUpPaymentsEvent, actions?.payments.url);

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', md: 'lg' }}
      dividers={false}
      body={
        <Box css={{ display: 'flex', flexDirection: 'column', gap: '600', alignItems: 'center', padding: '800' }}>
          <Heading level={2} textStyle="heading-sm" css={{ textAlign: 'center' }}>
            Your property was added
          </Heading>

          {!addedData ? (
            <>
              <Box css={{ display: 'flex', flexDirection: 'column', gap: '400', alignItems: 'center' }}>
                <Box
                  data-loading={true}
                  css={{ width: '300px', height: '200px', borderRadius: '600', flexShrink: '0', ...shimmerStyles }}
                />
                <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '100' }}>
                  <LoadingText isLoading={true} textStyle="body-bold" loadingWidth="200px">
                    &nbsp;
                  </LoadingText>
                  <LoadingText isLoading={true} textStyle="body-sm" loadingWidth="120px">
                    &nbsp;
                  </LoadingText>
                </Box>
              </Box>

              <Box css={{ display: 'flex', flexDirection: 'column', gap: '400', width: '100%' }}>
                <Heading level={2} textStyle="heading-xs" css={{ textAlign: 'center' }}>
                  What would you like to do next?
                </Heading>
                <UpsellSkeleton />
                <Box
                  css={{
                    display: 'grid',
                    gridTemplateColumns: { base: '1fr', md: 'repeat(2, 1fr)' },
                    gap: '600',
                  }}
                >
                  <UpsellSkeleton />
                  <UpsellSkeleton />
                  <UpsellSkeleton />
                  <UpsellSkeleton />
                </Box>
              </Box>
            </>
          ) : (
            <>
              <Box css={{ display: 'flex', flexDirection: 'column', gap: '400', alignItems: 'center' }}>
                <Box
                  data-testid="success-property-image"
                  css={{ width: '300px', height: '200px', borderRadius: '600', overflow: 'hidden', flexShrink: '0' }}
                >
                  <Image
                    src={RENTAL_HERO_ILLUSTRATION_URL}
                    alt=""
                    role="presentation"
                    width={300}
                    height={200}
                    priority
                    style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                  />
                </Box>

                <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Text
                    textStyle="body-bold"
                    css={{ textAlign: 'center', display: 'block' }}
                    data-testid="success-address"
                  >
                    {formattedAddress}
                  </Text>
                  <Text
                    textStyle="body-sm"
                    css={{ textAlign: 'center', display: 'block' }}
                    data-testid="success-property-type"
                  >
                    {propertyTypeLine}
                  </Text>
                </Box>
              </Box>

              <Box css={{ display: 'flex', flexDirection: 'column', gap: '400', width: '100%' }}>
                <Heading level={2} textStyle="heading-xs" css={{ textAlign: 'center' }}>
                  What would you like to do next?
                </Heading>

                {actions?.listing.isAllowed && (
                  <Upsell
                    data-testid="success-action-list-property"
                    elevated={false}
                    closeable={false}
                    media={<Icon render={<IconYardSignFilled />} />}
                    header={
                      <Heading level={3} textStyle="heading-xs">
                        List property for rent
                      </Heading>
                    }
                    body={
                      <Paragraph>
                        1 million visitors come to Zillow, Trulia, and HotPads every day — show up where they&apos;re
                        looking.
                      </Paragraph>
                    }
                    action={<TextButton onClick={handleCreateListingClick}>Create listing</TextButton>}
                  />
                )}

                <Box
                  css={{
                    display: 'grid',
                    gridTemplateColumns: { base: '1fr', md: 'repeat(2, 1fr)' },
                    gap: '600',
                  }}
                >
                  {actions?.tenants.isAllowed && (
                    <Upsell
                      data-testid="success-action-add-tenant"
                      elevated={false}
                      closeable={false}
                      media={<Icon render={<IconHouseUserFilled />} />}
                      header={
                        <Heading level={3} textStyle="heading-xs">
                          Add a tenant
                        </Heading>
                      }
                      body={
                        <Paragraph>Keep track of your residents, their lease terms, and contact details.</Paragraph>
                      }
                      action={
                        <TextButton onClick={() => onAddTenantClick(effectiveListingAlias ?? '')}>
                          Add a tenant
                        </TextButton>
                      }
                    />
                  )}

                  {actions?.applications.isAllowed && (
                    <Upsell
                      data-testid="success-action-invite-to-apply"
                      elevated={false}
                      closeable={false}
                      media={<Icon render={<IconProfileFilled />} />}
                      header={
                        <Heading level={3} textStyle="heading-xs">
                          Invite to apply
                        </Heading>
                      }
                      body={<Paragraph>Screen renters with Zillow&apos;s hassle-free applications.</Paragraph>}
                      action={<TextButton onClick={handleInviteToApplyClick}>Invite to apply</TextButton>}
                    />
                  )}

                  {actions?.leases.isAllowed && (
                    <Upsell
                      data-testid="success-action-add-lease"
                      elevated={false}
                      closeable={false}
                      media={<Icon render={<IconSignatureFilled />} />}
                      header={
                        <Heading level={3} textStyle="heading-xs">
                          Add a lease
                        </Heading>
                      }
                      body={<Paragraph>Upload a lease for signing, or just to keep your records organized.</Paragraph>}
                      action={<TextButton onClick={handleAddLeaseClick}>Add a lease</TextButton>}
                    />
                  )}

                  {actions?.payments.isAllowed && (
                    <Upsell
                      data-testid="success-action-set-up-payments"
                      elevated={false}
                      closeable={false}
                      media={<Icon render={<IconDollarSignCircleFilled />} />}
                      header={
                        <Heading level={3} textStyle="heading-xs">
                          Set up payments
                        </Heading>
                      }
                      body={<Paragraph>Collect rent at no cost to you, and in as few as 2 business days.</Paragraph>}
                      action={<TextButton onClick={handleSetUpPaymentsClick}>Set up payments</TextButton>}
                    />
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>
      }
    />
  );
};

export default PropertyAddedSuccessStep;
